import { HttpErrorResponse } from '@angular/common/http';
import {
  afterNextRender,
  Component,
  effect,
  forwardRef,
  inject,
  Injector,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { uniq, without } from 'lodash-es';
import { DateTime } from 'luxon';
import {
  catchError,
  EMPTY,
  firstValueFrom,
  forkJoin,
  interval,
  map,
  of,
  Subscription,
  switchMap,
  throwError
} from 'rxjs';
import { tap } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';
import { LoadingComponent } from '../../../component/loading/loading.component';
import { NavComponent } from '../../../component/nav/nav.component';
import { writePlugins } from '../../../form/plugins/plugins.component';
import { refForm, RefFormComponent } from '../../../form/ref/ref.component';
import { HasChanges } from '../../../guard/pending-changes.guard';
import { Ext } from '../../../model/ext';
import { Ref } from '../../../model/ref';
import { AdminService } from '../../../service/admin.service';
import { ExtService } from '../../../service/api/ext.service';
import { RefService } from '../../../service/api/ref.service';
import { ScrapeService } from '../../../service/api/scrape.service';
import { TaggingService } from '../../../service/api/tagging.service';
import { BookmarkService } from '../../../service/bookmark.service';
import { EditorService } from '../../../service/editor.service';
import { ModService } from '../../../service/mod.service';
import { OembedStore } from '../../../store/oembed';
import { Store } from '../../../store/store';
import { controlState, scrollToFirstInvalid } from '../../../util/form';
import { interestingTags } from '../../../util/format';
import { printError } from '../../../util/http';
import { getVisibilityTags } from '../../../util/tag';

@Component({
  selector: 'app-submit-web-page',
  templateUrl: './web.component.html',
  styleUrls: ['./web.component.scss'],
  host: { 'class': 'full-page-form' },
  imports: [
    ReactiveFormsModule,
    NavComponent,
    LoadingComponent,
    forwardRef(() => RefFormComponent),
  ],
})
export class SubmitWebPage implements HasChanges {
  private mod = inject(ModService);
  admin = inject(AdminService);
  private router = inject(Router);
  private store = inject(Store);
  private editor = inject(EditorService);
  private refs = inject(RefService);
  private exts = inject(ExtService);
  private ts = inject(TaggingService);
  private oembeds = inject(OembedStore);
  private scrape = inject(ScrapeService);
  bookmarks = inject(BookmarkService);
  private fb = inject(UntypedFormBuilder);


  private readonly injector = inject(Injector);

  readonly submitted = signal<boolean>(false);
  title = '';
  webForm: UntypedFormGroup;
  protected readonly webFormValid = controlState(() => this.webForm, c => c.valid);
  protected readonly webFormPristine = controlState(() => this.webForm, c => c.pristine);
  readonly serverError = signal<string[]>([]);

  readonly submitting = signal(false);
  private submittingSubscription?: Subscription;
  readonly saving = signal(false);
  private savingSubscription?: Subscription;
  readonly defaults = signal<{ url: string, ref: Partial<Ref> } | undefined>(undefined);
  readonly loadingDefaults = signal<Ext[]>([]);
  readonly defaultsLoaded = signal(false);
  readonly alreadyExists = signal<boolean>(false);

  private oldSubmit: string[] = [];
  private cursor?: string;
  private hasDraft = false;

  constructor() {
    const fb = this.fb;

    this.setTitle($localize`Submit: Web Link`);
    this.webForm = refForm(fb);
    if (this.admin.editing()) {
      interval(5_000).pipe(
        takeUntilDestroyed(),
      ).subscribe(() => {
        if (this.webForm.dirty) this.saveForLater();
      });
    }
  }

  async saveChanges() {
    if (this.admin.editing() && this.webForm.dirty) {
      return firstValueFrom(this.refs.saveEdit(this.writeRef(), this.cursor)
        .pipe(map(() => true), catchError(() => of(false))));
    }
    return !this.webForm?.dirty;
  }

  private readonly initializeView = afterNextRender(() => {
    this.url = this.store.submit.url()?.trim();
    const allTags = [...this.store.submit.tags(), ...(this.store.account.localTag() ? [this.store.account.localTag()] : [])];
    // Load the draft before the defaults, so neither the defaults nor a scraped title overwrite it
    (this.admin.editing() && this.url ? this.refs.getEditing(this.url) : of(undefined)).pipe(
      switchMap(draft => this.exts.getCachedExts(allTags).pipe(
        map(xs => xs.filter(x => x.config?.defaults) as Ext[]),
        switchMap(xs => {
          this.loadingDefaults.set(xs);
          return this.refs.getDefaults(...xs.map(x => x.tag))
        }),
        map(d => ({ draft, d })),
      )),
    ).subscribe(({ draft, d }) => {
      this.defaults.set(d);
      this.loadingDefaults.set([]);
      // Seed the location once so later tag changes keep any edits
      const location = this.store.submit.location();
      const point = location ? { type: 'Feature', geometry: { type: 'Point', coordinates: location } } : undefined;
      if (d) {
        this.oldSubmit = uniq([...allTags, ...Object.keys(d.ref.plugins || {})]);
        for (const k in d.ref.plugins) {
          if (k === this.store.submit.plugin()) continue;
          this.addPlugin(k, d.ref.plugins[k]);
        }
        this.refForm.setRef({
          ...d.ref,
          tags: point ? uniq([...this.oldSubmit, 'plugin/geo/point']) : this.oldSubmit,
          plugins: point ? { ...d.ref.plugins || {}, 'plugin/geo/point': point } : d.ref.plugins,
        });
      }
      if (this.store.account.localTag()) this.addTag(this.store.account.localTag());
      if (point && !d) this.addPlugin('plugin/geo/point', point);
      if (draft) {
        this.cursor = draft.modifiedString;
        this.hasDraft = true;
        this.refForm.setRef(draft.plugins?.['plugin/editing'] || {});
      }
      this.defaultsLoaded.set(true);
      effect(() => {
        this.store.submit.tags();
        this.store.account.localTag();
        this.store.submit.pluginUpload();
        this.store.submit.plugin();
        this.store.submit.title();
        this.store.submit.url();
        this.store.submit.repost();
        this.store.submit.source();
        this.store.submit.sources();
        untracked(() => {
          const tags = [...this.store.submit.tags(), ...(this.store.account.localTag() ? [this.store.account.localTag()] : [])];
          const added = without(tags, ...this.oldSubmit);
          const removed = without(this.oldSubmit, ...tags);
          if (added.length || removed.length) {
            this.oldSubmit = uniq([...without(this.oldSubmit, ...removed), ...added]);
            this.addTag(...this.oldSubmit);
          }
          if (this.store.submit.pluginUpload()) {
            this.addPlugin(this.store.submit.plugin(), { url: this.store.submit.pluginUpload() })
            if (this.store.submit.plugin() === 'plugin/image' || this.store.submit.plugin() === 'plugin/video') {
              this.addTag('plugin/thumbnail');
            }
          }
          if (this.admin.getPlugin('plugin/thumbnail') && (
            this.store.submit.tags().includes('plugin/video') ||
            this.store.submit.tags().includes('plugin/image'))) {
            this.addTag('plugin/thumbnail')
          }
          if (this.origin) {
            this.addTag('internal');
            this.setTitle($localize`Replicate Remote Origin`);
          } else if (this.feed) {
            this.addTag('internal');
            this.setTitle($localize`Submit: Feed`);
          }
          if (this.store.submit.title()) {
            this.webForm.get('title')!.setValue(this.store.submit.title());
          }
          let url = this.store.submit.url()?.trim();
          if (this.store.submit.repost()) {
            this.url = 'internal:' + uuid();
            this.addTag('plugin/repost');
            this.addSource(url);
          } else if (this.feed) {
            if (this.store.submit.tags().includes('public')) this.addFeedTags('public');
            this.addFeedTags(...interestingTags(this.store.submit.tags()));
            this.scrape.rss(url).pipe(
              switchMap(value => {
                if (!value) return of(value);
                return this.refs.page({ url: value, size: 1, query: this.store.account.origin() || '*', obsolete: null }).pipe(
                  map(page => page.content.length > 0 ? undefined : value),
                  catchError(() => of(value))
                );
              })
            ).subscribe(value => {
              if (value) {
                this.url = value;
                this.addTag('plugin/repost');
                this.addSource(url);
                if (url.startsWith('https://www.youtube.com/@') || url.startsWith('https://youtube.com/@')) {
                  this.refForm.scrapePlugins(url, { 'plugin/thumbnail': { radius: 24 } });
                  const username = url.substring(url.indexOf('@'));
                  if (!this.store.submit.title()) this.webForm.get('title')!.setValue(username);
                  const tag = username.toLowerCase().replace(/[^a-z0-9]+/, '');
                  this.addFeedTags(tag);
                } else {
                  this.refForm.scrapePlugins(url);
                  if (!this.store.submit.title() && !this.hasDraft) this.refForm.scrapeTitle();
                }
              } else {
                // No RSS URL found or found value already exists
                if (!this.store.submit.title() && !this.hasDraft) {
                  this.refForm.scrapeTitle();
                }
              }
            });
          } else {
            this.oembeds.get(url).subscribe(oembed => {
              if (!this.store.submit.title() && !this.hasDraft) this.refForm.scrapeTitle();
              if (!oembed) return;
              if (oembed?.thumbnail_url) {
                this.addPlugin('plugin/thumbnail', { url: oembed.thumbnail_url });
              }
              if (oembed?.author_url) {
                this.addSource(oembed?.author_url);
              }
              if (oembed.url && oembed.type === 'photo') {
                this.addPlugin('plugin/image', { url: oembed.url });
              } else {
                this.addTag('plugin/embed');
              }
              if (oembed?.provider_name === 'Twitter') {
                let comment = oembed.html!.replace(/(<([^>]+)>)/gi, "").trim().replace(/\s+/gi, ' ');
                if (comment.length > 140) comment = comment.substring(0, 139) + '…';
                this.webForm.get('comment')!.setValue(comment);
              }
            });
          }
          if (this.store.submit.source()) {
            this.store.submit.sources().map(s => this.addSource(s));
          }
        });
      }, { injector: this.injector });
    });
  });

  readonly refFormView = viewChild.required<RefFormComponent>('refForm');

  get refForm(): RefFormComponent {
    return this.refFormView();
  }

  get feed() {
    return !!this.webForm.value.tags.includes('plugin/script/feed');
  }

  get origin() {
    return !!this.webForm.value.tags.includes('+plugin/origin');
  }

  set url(value: string) {
    const plugins = this.admin.getPluginsForUrl(value);
    if (plugins.length) {
      this.addTag(...plugins.map(p => p.tag));
    }
    this.webForm.get('url')?.setValue(value);
  }

  get url() {
    return this.webForm.get('url')?.value;
  }

  saveForLater(leave = false) {
    const savedValue = JSON.stringify(this.webForm.value);
    this.saving.set(true);
    this.savingSubscription = this.refs.saveEdit(this.writeRef(), this.cursor)
      .pipe(catchError(err => {
        this.saving.set(false);
        return throwError(() => err);
      }))
      .subscribe(cursor => {
        this.saving.set(false);
        this.cursor = cursor;
        if (JSON.stringify(this.webForm.value) === savedValue) this.webForm.markAsPristine();
        if (leave) this.router.navigate(['/inbox/ref', 'plugin/editing']);
      });
    this.savingSubscription?.add(() => this.saving.set(false));
  }

  setTitle(title: string) {
    this.title = title;
    this.mod.setTitle(title);
  }

  addTag(...values: string[]) {
    for (const value of values) {
      this.refForm.tagsFormComponent().addTag(value);
    }
    this.submitted.set(false);
  }

  removeTag(...values: string[]) {
    for (const value of values) {
      this.refForm.tagsFormComponent().removeTag(value);
    }
    this.submitted.set(false);
  }

  addPlugin(tag: string, plugin: any) {
    this.refForm.tagsFormComponent().addTag(tag);
    this.refForm.pluginsFormComponent().setValue({
      ...this.webForm.value.plugins || {},
      [tag]: plugin,
    });
    this.submitted.set(false);
  }

  addSource(value = '') {
    this.refForm.sourcesFormComponent().addLink(value);
    this.submitted.set(false);
  }

  addAlt(value = '') {
    this.refForm.altsFormComponent().addLink(value);
    this.submitted.set(false);
  }

  syncEditor() {
    this.editor.syncEditor(this.fb, this.webForm);
  }

  writeRef(publish = false) {
    return {
      ...this.webForm.value,
      url: this.url, // Need to pull separately since control is locked
      origin: this.store.account.origin(),
      published: this.webForm.value.published ? DateTime.fromISO(this.webForm.value.published) : publish ? DateTime.now() : undefined,
      plugins: writePlugins(this.webForm.value.tags, this.webForm.value.plugins),
    };
  }

  submit() {
    if (this.saving()) {
      this.savingSubscription?.add(() => this.submit());
      return;
    }
    if (!this.defaultsLoaded() || this.alreadyExists()) {
      return;
    }
    this.serverError.set([]);
    this.submitted.set(true);
    this.webForm.markAllAsTouched();
    this.syncEditor();
    if (!this.webForm.valid) {
      scrollToFirstInvalid();
      return;
    }
    const published = this.webForm.value.published ? DateTime.fromISO(this.webForm.value.published) : DateTime.now();
    const ref = this.writeRef(true);
    const finalTags = ref.tags;
    this.submitting.set(true);
    this.submittingSubscription = (this.cursor ? this.refs.update({ ...ref, modifiedString: this.cursor }) : this.refs.create(ref)).pipe(
      catchError((res: HttpErrorResponse) => {
        if (res.status !== 409) return throwError(() => res);
        this.submitting.set(false);
        this.serverError.set(printError(res));
        this.alreadyExists.set(true);
        return EMPTY;
      }),
      tap(() => {
        if (this.admin.getPlugin('plugin/user/vote/up')) {
          this.ts.createResponse('plugin/user/vote/up', this.url).subscribe();
        }
      }),
      switchMap(res => {
        const finalVisibilityTags = getVisibilityTags(finalTags);
        if (!finalVisibilityTags.length) return of(res);
        const taggingOps = this.refForm.completedUploads()
          .map(upload => this.ts.patch(finalVisibilityTags, upload.url, upload.origin));
        if (!taggingOps.length) return of(res);
        return forkJoin(taggingOps).pipe(map(() => res));
      }),
      catchError((res: HttpErrorResponse) => {
        this.submitting.set(false);
        this.serverError.set(printError(res));
        return throwError(() => res);
      }),
    ).subscribe(() => {
      this.submitting.set(false);
      this.webForm.markAsPristine();
      this.refForm.completedUploads.set([]);

      this.router.navigate(['/ref', this.url], { queryParams: { published }, replaceUrl: true});
    });
    this.submittingSubscription?.add(() => this.submitting.set(false));
  }

  prepareRepost() {
    const url = this.url;
    this.url = 'internal:' + uuid();
    this.addTag('plugin/repost');
    const sources = (this.webForm.value.sources || []).filter((source: string) => source !== url);
    this.refForm.sourcesFormComponent().setLinks([url, ...sources]);
    this.webForm.markAsDirty();
    this.alreadyExists.set(false);
    this.serverError.set([]);
    this.submitted.set(false);
  }

  private addFeedTags(...tags: string[]) {
    if (!this.feed) return;
    tags = tags.filter(t => t !== 'plugin/script/feed');
    const ref = this.webForm.value || {};
    ref.plugins ||= {};
    ref.plugins['plugin/script/feed'] ||= {};
    ref.plugins['plugin/script/feed'].addTags = uniq([...ref.plugins['plugin/script/feed'].addTags || [], ...tags]);
    this.refForm.pluginsFormComponent().setValue(ref.plugins);
  }
}
